import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getAmplifyClient } from '@/services/amplifyClient'

import { createCatalog, deleteCatalog, updateCatalog } from '../graphql'

type CatalogParams = {
  upsertType: 'create' | 'update' | 'delete'
  input: Partial<any>
}

const client = getAmplifyClient()

export const useUpsertCatalog = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['upsertCatalog'],
    mutationFn: upsertCatalog,
    onMutate: async ({ upsertType, input }) => {
      queryClient.setQueryData(
        // Keep key consistent with useCatalogs (which does not include locale)
        ['catalogs', input.projectCatalogsId],
        (oldData: any) => {
          switch (upsertType) {
            case 'delete':
              return oldData.filter((catalog) => catalog.id !== input.id)
            case 'create':
              return [...(oldData ?? []), input]
            case 'update':
            default:
              return oldData.map((catalog) =>
                catalog.id === input.id ? input : catalog,
              )
          }
        },
      )
    },
  })
}

const getCatalogMutation = (upsertType: string): string => {
  switch (upsertType) {
    case 'create':
      return createCatalog
    case 'delete':
      return deleteCatalog
    case 'update':
    default:
      return updateCatalog
  }
}

const fieldsToRemove = ['createdAt', 'updatedAt']

const upsertCatalog = async ({ upsertType, input }: CatalogParams) => {
  const catalog = { ...input }
  fieldsToRemove.forEach((field) => delete catalog[field])

  const mutation = getCatalogMutation(upsertType)

  return client.graphql({ query: mutation, variables: { input: catalog } })
}
